/**
 * AppICEMAP — ConfirmDialog
 * Promise-based confirmation modal, used for destructive actions.
 *
 *   const ok = await confirmAction({
 *     title: "Borrar reporte",
 *     message: "Esta acción no se puede deshacer.",
 *     confirmLabel: "Borrar",
 *     danger: true,
 *   });
 *
 * Resolves true on confirm, false on cancel / Escape / backdrop click.
 * Only one dialog can be open at a time; a second call reuses the same node.
 */

/** @type {HTMLElement|null} */
let current = null;

/**
 * @typedef {object} ConfirmOptions
 * @property {string} title
 * @property {string} [message]
 * @property {string} [detail] - Secondary line, e.g. the item being deleted
 * @property {string} [confirmLabel="Confirmar"]
 * @property {string} [cancelLabel="Cancelar"]
 * @property {boolean} [danger=false] - Style the confirm button as destructive
 */

/**
 * Show the dialog.
 * @param {ConfirmOptions} options
 * @returns {Promise<boolean>}
 */
export function confirmAction(options) {
	const {
		title,
		message = "",
		detail = "",
		confirmLabel = "Confirmar",
		cancelLabel = "Cancelar",
		danger = false,
	} = options || {};

	// Never stack dialogs
	if (current) {
		current.remove();
		current = null;
	}

	return new Promise((resolve) => {
		const overlay = document.createElement("div");
		overlay.className = "confirm-dialog";
		overlay.innerHTML = `
			<div class="confirm-dialog__backdrop"></div>
			<div class="confirm-dialog__panel" role="alertdialog" aria-modal="true"
			     aria-labelledby="confirm-dialog-title">
				<div class="confirm-dialog__icon${danger ? " confirm-dialog__icon--danger" : ""}">
					<i class="ph ${danger ? "ph-trash" : "ph-question"}" aria-hidden="true"></i>
				</div>
				<h3 class="confirm-dialog__title" id="confirm-dialog-title">${escapeHtml(title)}</h3>
				${message ? `<p class="confirm-dialog__message">${escapeHtml(message)}</p>` : ""}
				${detail ? `<p class="confirm-dialog__detail">${escapeHtml(detail)}</p>` : ""}
				<div class="confirm-dialog__actions">
					<button class="btn btn--ghost" data-action="cancel">${escapeHtml(cancelLabel)}</button>
					<button class="btn ${danger ? "btn--danger" : "btn--primary"}" data-action="confirm">
						${escapeHtml(confirmLabel)}
					</button>
				</div>
			</div>
		`;

		document.body.appendChild(overlay);
		current = overlay;

		const previouslyFocused = document.activeElement;
		const confirmBtn = overlay.querySelector('[data-action="confirm"]');
		const cancelBtn = overlay.querySelector('[data-action="cancel"]');

		// Focus lands on Cancel: the safe option for a destructive prompt
		requestAnimationFrame(() => cancelBtn?.focus());

		const close = (result) => {
			document.removeEventListener("keydown", onKeydown, true);
			overlay.remove();
			if (current === overlay) current = null;
			if (previouslyFocused?.focus) previouslyFocused.focus();
			resolve(result);
		};

		function onKeydown(e) {
			if (e.key === "Escape") {
				e.preventDefault();
				close(false);
				return;
			}
			// Keep Tab inside the dialog
			if (e.key === "Tab") {
				const focusables = [cancelBtn, confirmBtn];
				const idx = focusables.indexOf(document.activeElement);
				if (idx !== -1) {
					e.preventDefault();
					const next = e.shiftKey ? idx - 1 : idx + 1;
					focusables[(next + focusables.length) % focusables.length].focus();
				}
			}
		}

		document.addEventListener("keydown", onKeydown, true);
		confirmBtn?.addEventListener("click", () => close(true));
		cancelBtn?.addEventListener("click", () => close(false));
		overlay
			.querySelector(".confirm-dialog__backdrop")
			?.addEventListener("click", () => close(false));
	});
}

function escapeHtml(str) {
	const div = document.createElement("div");
	div.textContent = str ?? "";
	return div.innerHTML;
}
